import { DotDotDot } from "@rosette/web_components/src/game/timing/DotDotDot.js";
import { AnimatePresence, motion } from "framer-motion";
import { fadeInOut } from "@/utils/fadeInOut.js";
import type { ReactNode } from "react";
import React, { useRef } from "react";
import { cn } from "@rosette/ts/src/util/cn.js";
import { useAssetLoader } from "@/components/assets/AssetContext.js";
import { useOptionalRune, useRune } from "@rosette/react_utils/src/useRune.js";
import { PopupButton } from "@/components/generic/popup/PopupButton.js";
import { VolumeIcon } from "@rosette/web_components/src/icon/VolumeIcon.js";
import type { PopupFunctions } from "@/components/generic/popup/Popup.js";
import { VolumeOffIcon } from "@rosette/web_components/src/icon/VolumeOffIcon.js";
import { MusicControls } from "@/components/game/menu/ingame/MusicControls.js";
import { MenuType } from "@rosette/web_components/src/game/menu/MenuType.js";
import { useGamePreferences } from "@/app/(api)/api/getGamePreferences/useGamePreferences.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";


const MUSIC_ICON = "inline h-[1.25em] mr-[0.4em] fill-current align-middle";


interface GameStatusProps {
    className?: string;
    statusText: string | null;
    dotDotDot: boolean;
    activeMenu: MenuType | null;
    setPreferredMenu: (menu: MenuType | null) => void;
}

export function GameStatus({
    className, statusText, dotDotDot, activeMenu, setPreferredMenu,
}: GameStatusProps) {

    const assetLoader = useAssetLoader();
    const user = useOptionalUser();
    const { preferences } = useGamePreferences(user);
    const audioSettings = useRune(assetLoader.getAudioSettings());
    const activeMusicAsset = useRune(assetLoader.getActiveMusicTrack()).getOrNull();
    const activeMusicTrack = activeMusicAsset?.getTrack();
    const musicPlaying = !!useOptionalRune(activeMusicAsset?.getPlayingRune());

    const lines: ReactNode[] = [];
    if (statusText && !activeMenu) {
        lines.push(
            <motion.div
                key="status"
                {...fadeInOut()}>

                {statusText}
                <DotDotDot enabled={dotDotDot} />
            </motion.div>,
        );
    }

    const musicPopupRef = useRef<PopupFunctions | null>(null);
    if (activeMusicAsset && activeMusicTrack && !preferences.hideMusicControls) {
        lines.push(
            <motion.div
                key="music"
                {...fadeInOut()}>

                <PopupButton
                    contentClassName="w-[340px] min-w-[340px] max-w-[340px]"
                    buttonClassName="align-middle"
                    darkMode={true}
                    includeExtraSpaceOnMobile={true}
                    minPopupWidthPx={320}
                    popupRef={musicPopupRef}
                    buttonContent={(
                        <>
                            {musicPlaying && (
                                <VolumeIcon className={MUSIC_ICON} />
                            )}
                            {!musicPlaying && (
                                <VolumeOffIcon className={MUSIC_ICON} />
                            )}
                            <span className="align-middle">
                                Music
                            </span>
                        </>
                    )}>

                    <MusicControls
                        assetLoader={assetLoader}
                        audioSettings={audioSettings}
                        activeAsset={activeMusicAsset}
                        activeTrack={activeMusicTrack}
                        isPlaying={musicPlaying}
                        closeControls={() => musicPopupRef.current?.close()}
                        openSettings={() => setPreferredMenu(MenuType.PREFERENCES)} />
                </PopupButton>
            </motion.div>,
        );
    }

    return (
        <AnimatePresence mode="wait">
            {lines.length > 0 && (
                <motion.div
                    key="container"
                    className={cn(
                        "flex flex-col items-center pointer-events-none",
                        className ?? null,
                    )}
                    {...fadeInOut()}>

                    <div className={cn(
                        "flex flex-col items-center pointer-events-auto",
                        "py-1 px-3 md:py-2 md:px-4 rounded-b-xl",
                        "text-sm md:text-base font-bold text-strong bg-surface-neutral",
                        "shadow-[0_2px_6px_rgba(0,0,0,0.8)]",
                    )}>
                        <AnimatePresence>
                            {lines}
                        </AnimatePresence>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
